import React, { useState, useRef, useEffect } from 'react';
import { uploadFile } from '../storageUtils';

interface AudioRecorderProps {
    onAudioReady: (blob: Blob | null, previewUrl: string, duration: number, startTime: number, endTime: number, answerStartTime: number, answerEndTime: number) => void;
    initialAudioUrl?: string;
    initialStartTime?: number;
    initialEndTime?: number;
    initialAnswerStartTime?: number;
    initialAnswerEndTime?: number;
}

// FAKE_WAVEFORM constant outside
const WAVEFORM_HEIGHTS = [
    25, 35, 20, 45, 60, 40, 30, 50, 60, 45, 
    20, 35, 55, 65, 40, 25, 30, 50, 40, 60, 
    35, 20, 45, 30, 55, 60, 40, 25, 35, 50, 
    45, 20, 30, 50, 60, 35, 45, 20, 40, 50,
    30, 45, 60, 35, 20, 40, 55, 45, 25, 35
];

const formatTimeStr = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
};

interface TrimmerControlProps {
    title: string;
    description: string;
    startTime: number;
    endTime: number;
    audioDuration: number;
    previewTime: number;
    isPreviewPlaying: boolean;
    dragTarget: string | null;
    onDragStart: (target: 'left' | 'right') => void;
    onTogglePlay: () => void;
    trimmerRef: React.RefObject<HTMLDivElement | null>;
}

const TrimmerControl: React.FC<TrimmerControlProps> = ({
    title, description, startTime, endTime, audioDuration, previewTime, isPreviewPlaying, dragTarget, onDragStart, onTogglePlay, trimmerRef
}) => {
    return (
        <div className="flex flex-col gap-2 w-full">
            <h3 className="text-white font-bold text-sm tracking-wide">{title}</h3>
            <p className="text-gray-400 text-xs mb-2">{description}</p>
            <div className="flex items-center w-full gap-4">
                <button 
                    type="button"
                    onClick={onTogglePlay}
                    className="hover:scale-105 transition-transform flex items-center justify-center shrink-0 p-1"
                >
                    <i className={`ph-fill ${isPreviewPlaying ? 'ph-pause' : 'ph-play'} text-4xl text-pink-500 drop-shadow-md`}></i>
                </button>
                <div className="text-sm font-mono text-white/70">
                    {formatTimeStr(isPreviewPlaying ? previewTime : startTime)} / {formatTimeStr(endTime)}
                </div>
            </div>

            {/* Interactive Trimmer */}
            <div 
                className="relative w-full h-16 bg-zinc-800/80 rounded-xl overflow-hidden touch-none select-none my-2"
                ref={trimmerRef}
            >
                {/* Background Waveform (Inactive) */}
                <div className="absolute inset-x-2 inset-y-0 flex items-center justify-between gap-[2px]">
                    {WAVEFORM_HEIGHTS.map((h, i) => {
                        const barPos = (i / WAVEFORM_HEIGHTS.length) * audioDuration;
                        const isActive = barPos >= startTime && barPos <= endTime;
                        return (
                            <div 
                                key={`bg-${i}`} 
                                className={`flex-1 rounded-full ${isActive ? 'bg-white' : 'bg-white/20'}`} 
                                style={{ height: `${h}%` }}
                            ></div>
                        );
                    })}
                </div>

                {/* Active Region Overlay (Gradient Border) */}
                <div 
                    className="absolute inset-y-0 z-10"
                    style={{ 
                        left: `${(startTime / audioDuration) * 100}%`, 
                        right: `${100 - (endTime / audioDuration) * 100}%`
                    }}
                >
                    <div className="absolute inset-0 rounded-lg p-[3px] bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-600 opacity-90 pointer-events-none">
                        {/* Inner dark area to allow waveform to show clearly */}
                        <div className="absolute inset-[3px] bg-black/30 rounded-md"></div>
                    </div>
                    
                    {/* Playhead indicator */}
                    {isPreviewPlaying && (
                        <div 
                            className="absolute top-0 bottom-0 w-[2px] bg-white shadow-[0_0_8px_rgba(255,255,255,0.8)] z-20 pointer-events-none"
                            style={{ left: `${((previewTime - startTime) / (endTime - startTime)) * 100}%` }}
                        ></div>
                    )}
                    
                    {/* Left Drag Handle */}
                    <div 
                        className="absolute top-0 bottom-0 left-0 w-8 -ml-4 cursor-col-resize flex items-center justify-center group pointer-events-auto touch-none z-30"
                        onPointerDown={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            onDragStart('left');
                        }}
                    >
                        <div className={`w-1.5 h-6 bg-white rounded-full shadow-md transition-transform ${dragTarget === 'left' ? 'scale-y-125' : 'group-hover:scale-125'}`}></div>
                    </div>
                    
                    {/* Right Drag Handle */}
                    <div 
                        className="absolute top-0 bottom-0 right-0 w-8 -mr-4 cursor-col-resize flex items-center justify-center group pointer-events-auto touch-none z-30"
                        onPointerDown={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            onDragStart('right');
                        }}
                    >
                        <div className={`w-1.5 h-6 bg-white rounded-full shadow-md transition-transform ${dragTarget === 'right' ? 'scale-y-125' : 'group-hover:scale-125'}`}></div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const AudioRecorder: React.FC<AudioRecorderProps> = ({ onAudioReady, initialAudioUrl, initialStartTime, initialEndTime, initialAnswerStartTime, initialAnswerEndTime }) => {
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [audioUrl, setAudioUrl] = useState(initialAudioUrl || '');
    const [audioDuration, setAudioDuration] = useState(30);
    const [startTime, setStartTime] = useState(initialStartTime || 0);
    const [endTime, setEndTime] = useState(initialEndTime || 30);
    const [answerStartTime, setAnswerStartTime] = useState(initialAnswerStartTime || 0);
    const [answerEndTime, setAnswerEndTime] = useState(initialAnswerEndTime || 30);
    const [error, setError] = useState('');
    
    // New preview and trimmer states
    const [previewTime, setPreviewTime] = useState(0);
    const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
    
    const [answerPreviewTime, setAnswerPreviewTime] = useState(0);
    const [isAnswerPreviewPlaying, setIsAnswerPreviewPlaying] = useState(false);

    const [dragTarget, setDragTarget] = useState<'left' | 'right' | 'answer-left' | 'answer-right' | null>(null);
    const trimmerRef = useRef<HTMLDivElement>(null);
    const answerTrimmerRef = useRef<HTMLDivElement>(null);
    
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const chunksRef = useRef<Blob[]>([]);
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const answerAudioRef = useRef<HTMLAudioElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // ... existing code ...
    useEffect(() => {
        if (!dragTarget) return;
        
        const handlePointerMove = (e: PointerEvent) => {
            const isAnswer = dragTarget === 'answer-left' || dragTarget === 'answer-right';
            const targetRef = isAnswer ? answerTrimmerRef : trimmerRef;
            if (!targetRef.current) return;
            const rect = targetRef.current.getBoundingClientRect();
            const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
            const val = (x / rect.width) * audioDuration;

            if (dragTarget === 'left') {
                let newVal = val;
                if (endTime - newVal > 30) newVal = endTime - 30;
                if (newVal >= endTime - 1) newVal = Math.max(0, endTime - 1);
                setStartTime(Math.max(0, newVal));
            } else if (dragTarget === 'right') {
                let newVal = val;
                if (newVal - startTime > 30) newVal = startTime + 30;
                if (newVal <= startTime + 1) newVal = Math.min(audioDuration, startTime + 1);
                setEndTime(Math.min(audioDuration, newVal));
            } else if (dragTarget === 'answer-left') {
                let newVal = val;
                if (answerEndTime - newVal > 30) newVal = answerEndTime - 30;
                if (newVal >= answerEndTime - 1) newVal = Math.max(0, answerEndTime - 1);
                setAnswerStartTime(Math.max(0, newVal));
            } else if (dragTarget === 'answer-right') {
                let newVal = val;
                if (newVal - answerStartTime > 30) newVal = answerStartTime + 30;
                if (newVal <= answerStartTime + 1) newVal = Math.min(audioDuration, answerStartTime + 1);
                setAnswerEndTime(Math.min(audioDuration, newVal));
            }
        };
        
        const handlePointerUp = () => setDragTarget(null);
        
        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp);
        
        return () => {
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
        };
    }, [dragTarget, endTime, startTime, answerStartTime, answerEndTime, audioDuration]);

    useEffect(() => {
        if (isRecording) {
            timerRef.current = setInterval(() => {
                setRecordingTime(prev => {
                    if (prev >= 30) {
                        stopRecording();
                        return prev;
                    }
                    return prev + 1;
                });
            }, 1000);
        } else {
            if (timerRef.current) clearInterval(timerRef.current);
        }
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [isRecording]);

    const startRecording = async () => {
        try {
            setError('');
            
            // Check if device media API is supported at all
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                setError('Microphone access is not supported on this device/setting. Please check permissions or disable Lockdown Mode.');
                return;
            }

            if (typeof MediaRecorder === 'undefined') {
                setError('Recording is not supported in this browser environment.');
                return;
            }

            const stream = await navigator.mediaDevices.getUserMedia({ audio: true }).catch(err => {
                throw new Error('Microphone permission denied or device busy: ' + err.message);
            });

            // Set up correct MIME Type for standard compatibility (especially iOS Safari which requires mp4/aac)
            let options = {};
            if (MediaRecorder.isTypeSupported) {
                if (MediaRecorder.isTypeSupported('audio/mp4')) {
                    options = { mimeType: 'audio/mp4' };
                } else if (MediaRecorder.isTypeSupported('audio/aac')) {
                    options = { mimeType: 'audio/aac' };
                } else if (MediaRecorder.isTypeSupported('audio/webm')) {
                    options = { mimeType: 'audio/webm' };
                }
            }

            const mediaRecorder = new MediaRecorder(stream, options);
            mediaRecorderRef.current = mediaRecorder;
            chunksRef.current = [];

            mediaRecorder.ondataavailable = (e) => {
                if (e.data.size > 0) chunksRef.current.push(e.data);
            };

            mediaRecorder.onstop = () => {
                // Use the correct mimeType from the recorder (essential for iOS which uses mp4 instead of webm)
                const mimeType = mediaRecorderRef.current?.mimeType || 'audio/mp4';
                const blob = new Blob(chunksRef.current, { type: mimeType });
                const url = URL.createObjectURL(blob);
                setAudioUrl(url);
                
                // Immediately pass the blob so form state is preserved even if metadata fails
                onAudioReady(blob, url, 30, 0, 30, 0, 30);
                
                // Get duration
                const tempAudio = new Audio(url);
                tempAudio.onloadedmetadata = () => {
                    const dur = Math.min(30, Math.ceil(tempAudio.duration || 30));
                    setAudioDuration(dur);
                    setStartTime(0);
                    setEndTime(dur);
                    setAnswerStartTime(0);
                    setAnswerEndTime(dur);
                    onAudioReady(blob, url, dur, 0, dur, 0, dur);
                };
            };

            mediaRecorder.start();
            setIsRecording(true);
            setRecordingTime(0);
            setAudioUrl('');
        } catch (err: any) {
            console.error(err);
            setError(err.message || 'Microphone access denied or error occurred.');
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            mediaRecorderRef.current.stream.getTracks().forEach(t => t.stop());
        }
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.size > 10 * 1024 * 1024) {
                setError('File too large (max 10MB).');
                return;
            }
            const url = URL.createObjectURL(file);
            setAudioUrl(url);
            
            // Immediately pass the file so form state is preserved even if metadata fails
            onAudioReady(file, url, 30, 0, 30, 0, 30);

            const tempAudio = new Audio(url);
            tempAudio.onloadedmetadata = () => {
                const dur = Math.ceil(tempAudio.duration || 30);
                const boundedEnd = Math.min(dur, 30);
                setAudioDuration(dur);
                setStartTime(0);
                setEndTime(boundedEnd);
                setAnswerStartTime(0);
                setAnswerEndTime(boundedEnd);
                onAudioReady(file, url, dur, 0, boundedEnd, 0, boundedEnd);
            };
        }
    };

    const onTimeChange = () => {
        onAudioReady(null, audioUrl, audioDuration, startTime, endTime, answerStartTime, answerEndTime); // Passing null blob means just use existing url/file
    };

    useEffect(() => {
        if (audioUrl) onTimeChange();
    }, [startTime, endTime, answerStartTime, answerEndTime]);

    const formatTime = (secs: number) => {
        const m = Math.floor(secs / 60);
        const s = secs % 60;
        return `${m}:${s < 10 ? '0' : ''}${s}`;
    };

    return (
        <div className="bg-black/20 p-4 rounded-xl border border-white/10 text-center space-y-4">
            <h3 className="text-white font-bold text-lg">Audio Puzzle (Max 30s)</h3>
            
            {!audioUrl && !isRecording && (
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                    <button 
                        type="button"
                        onClick={startRecording}
                        className="bg-red-500 hover:bg-red-600 text-white font-bold py-3 px-6 rounded-full flex items-center gap-2"
                    >
                        <i className="ph-fill ph-microphone"></i> Start Recording
                    </button>
                    <span className="text-gray-400">or</span>
                    <button 
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="bg-zinc-700 hover:bg-zinc-600 text-white font-bold py-3 px-6 rounded-full flex items-center gap-2"
                    >
                        <i className="ph-fill ph-upload-simple"></i> Upload Audio
                    </button>
                    <input 
                        type="file" 
                        accept="audio/*" 
                        ref={fileInputRef}
                        className="hidden" 
                        onChange={handleFileUpload} 
                    />
                </div>
            )}

            {isRecording && (
                <div className="space-y-4">
                    <div className="flex justify-center items-center gap-2 text-red-500 font-bold text-xl animate-pulse">
                        <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                        Recording... {formatTime(recordingTime)} / 0:30
                    </div>
                    <button 
                        type="button"
                        onClick={stopRecording}
                        className="bg-zinc-800 border-2 border-red-500 text-red-500 font-bold py-2 px-6 rounded-full"
                    >
                        Stop Recording
                    </button>
                </div>
            )}

            {error && <p className="text-red-400 text-sm font-bold">{error}</p>}

            {audioUrl && !isRecording && (
                <div className="space-y-4">
                    <audio 
                        src={audioUrl} 
                        controls 
                        className="w-full h-12 rounded-full hidden" 
                        ref={audioRef}
                        playsInline
                        onLoadedMetadata={(e) => {
                            const dur = e.currentTarget.duration;
                            if (dur < 30 || audioDuration === 30) {
                                setAudioDuration(dur);
                                setEndTime(prev => Math.min(prev, dur));
                            }
                        }}
                        onTimeUpdate={(e) => {
                            const a = e.currentTarget;
                            setPreviewTime(a.currentTime);
                            if (a.currentTime >= endTime) {
                                a.pause();
                                a.currentTime = startTime;
                                setPreviewTime(startTime);
                                setIsPreviewPlaying(false);
                            }
                        }}
                        onPlay={() => setIsPreviewPlaying(true)}
                        onPause={() => setIsPreviewPlaying(false)}
                        onEnded={() => {
                            setIsPreviewPlaying(false);
                            setPreviewTime(startTime);
                        }}
                    />

                    <audio 
                        src={audioUrl} 
                        className="hidden" 
                        ref={answerAudioRef}
                        playsInline
                        onLoadedMetadata={(e) => {
                            const dur = e.currentTarget.duration;
                            if (dur < 30 || audioDuration === 30) {
                                setAnswerEndTime(prev => Math.min(prev, dur));
                            }
                        }}
                        onTimeUpdate={(e) => {
                            const a = e.currentTarget;
                            setAnswerPreviewTime(a.currentTime);
                            if (a.currentTime >= answerEndTime) {
                                a.pause();
                                a.currentTime = answerStartTime;
                                setAnswerPreviewTime(answerStartTime);
                                setIsAnswerPreviewPlaying(false);
                            }
                        }}
                        onPlay={() => setIsAnswerPreviewPlaying(true)}
                        onPause={() => setIsAnswerPreviewPlaying(false)}
                        onEnded={() => {
                            setIsAnswerPreviewPlaying(false);
                            setAnswerPreviewTime(answerStartTime);
                        }}
                    />

                    <div className="flex flex-col gap-6 bg-zinc-900/50 p-4 rounded-xl">
                        <TrimmerControl 
                            title="Puzzle Audio (Max 30s)"
                            description="This is the clip users will hear to guess the answer."
                            startTime={startTime}
                            endTime={endTime}
                            audioDuration={audioDuration}
                            previewTime={previewTime}
                            isPreviewPlaying={isPreviewPlaying}
                            dragTarget={dragTarget}
                            onDragStart={(target) => setDragTarget(target)}
                            onTogglePlay={() => {
                                if (audioRef.current) {
                                    if (isPreviewPlaying) {
                                        audioRef.current.pause();
                                    } else {
                                        if (isAnswerPreviewPlaying) answerAudioRef.current?.pause();
                                        if (audioRef.current.readyState === 0) {
                                            audioRef.current.load();
                                        }
                                        try {
                                            audioRef.current.currentTime = previewTime > startTime && previewTime < endTime ? previewTime : startTime;
                                        } catch (e) {
                                            console.warn("Error setting currentTime, ignoring:", e);
                                        }
                                        const playPromise = audioRef.current.play();
                                        if (playPromise !== undefined) {
                                            playPromise.catch(err => console.error("Audio play failed:", err));
                                        }
                                    }
                                }
                            }}
                            trimmerRef={trimmerRef}
                        />

                        <div className="border-t border-white/10 my-1 w-full"></div>

                        <TrimmerControl 
                            title="Correct Guess Audio (Max 30s)"
                            description="This plays after a correct guess. Drag handles to trim."
                            startTime={answerStartTime}
                            endTime={answerEndTime}
                            audioDuration={audioDuration}
                            previewTime={answerPreviewTime}
                            isPreviewPlaying={isAnswerPreviewPlaying}
                            dragTarget={dragTarget}
                            onDragStart={(target) => setDragTarget(target === 'left' ? 'answer-left' : 'answer-right')}
                            onTogglePlay={() => {
                                if (answerAudioRef.current) {
                                    if (isAnswerPreviewPlaying) {
                                        answerAudioRef.current.pause();
                                    } else {
                                        if (isPreviewPlaying) audioRef.current?.pause();
                                        if (answerAudioRef.current.readyState === 0) {
                                            answerAudioRef.current.load();
                                        }
                                        try {
                                            answerAudioRef.current.currentTime = answerPreviewTime > answerStartTime && answerPreviewTime < answerEndTime ? answerPreviewTime : answerStartTime;
                                        } catch (e) {
                                            console.warn("Error setting currentTime, ignoring:", e);
                                        }
                                        const playPromise = answerAudioRef.current.play();
                                        if (playPromise !== undefined) {
                                            playPromise.catch(err => console.error("Audio play failed:", err));
                                        }
                                    }
                                }
                            }}
                            trimmerRef={answerTrimmerRef}
                        />
                    </div>

                    <button 
                        type="button"
                        onClick={() => {
                            setAudioUrl('');
                            onAudioReady(null, '', 0, 0, 0, 0, 0);
                            setIsPreviewPlaying(false);
                            if (audioRef.current) audioRef.current.pause();
                        }}
                        className="text-red-400 text-sm font-bold underline"
                    >
                        Remove/Retake
                    </button>
                </div>
            )}
        </div>
    );
};

export default AudioRecorder;
